import type { ComponentProps, ReactNode } from 'react'
import { useId, useState } from 'react'
import { cn } from './cn.js'

// 16px, not the 15px body size: anything smaller makes iOS Safari zoom toward
// the input on focus, and the page never zooms back on its own.
const inputBase =
  'w-full rounded-[var(--radius-control)] border border-line bg-surface px-3 text-[1rem] text-ink ' +
  'placeholder:text-mute transition-colors duration-[140ms] focus:border-line-strong ' +
  'disabled:opacity-50'

export interface FieldProps extends Omit<ComponentProps<'input'>, 'children'> {
  label: string
  hint?: ReactNode
  error?: string | null
  /** Unit rendered inside the trailing edge of the control (g, kcal, ml). */
  suffix?: string
}

export function Field({ label, hint, error, suffix, className, id, ...props }: FieldProps) {
  const generated = useId()
  const inputId = id ?? generated
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-sm text-dim">
        {label}
      </label>
      <div className="relative">
        <input
          id={inputId}
          className={cn(
            inputBase,
            'h-11',
            suffix != null && 'pr-12',
            error != null && 'border-danger',
            className,
          )}
          aria-invalid={error != null || undefined}
          {...props}
        />
        {suffix != null && (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-mute">
            {suffix}
          </span>
        )}
      </div>
      {error != null ? (
        <p className="text-sm text-danger">{error}</p>
      ) : (
        hint != null && <p className="text-sm text-mute">{hint}</p>
      )}
    </div>
  )
}

export interface NumberFieldProps extends Omit<FieldProps, 'onChange' | 'value' | 'type'> {
  value: number | null
  onChange: (value: number | null) => void
}

/**
 * Decimal entry for quantities. `inputMode="decimal"` is what gets the numeric
 * keypad on a phone.
 *
 * What is typed is held as text for as long as the field has focus, and only
 * the number behind it is reported upwards. Without that, a decimal could not
 * be typed at all: "1," normalises to "1.", parses to 1, and comes straight
 * back as "1" with the separator deleted — every time, so the field could never
 * get past the comma. On blur the text is dropped and the canonical value shows
 * through, which is also what puts a pasted "1.5" back as "1,5".
 *
 * Nothing this field asks for can be negative — grams, millilitres, calories,
 * a body weight — and the minus sign is one keystroke away on every keyboard
 * this runs on. A "-150 g" chicken breast passed every commit guard in the app
 * (they all test for truthiness) and logged −248 kcal, which raised the day's
 * remaining calories instead of spending them. Rejecting it here fixes it in
 * one place rather than in each of the nine sheets.
 */
export function NumberField({ value, onChange, ...rest }: NumberFieldProps) {
  const [typed, setTyped] = useState<string | null>(null)

  return (
    <Field
      {...rest}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={typed ?? (value == null ? '' : String(value).replace('.', ','))}
      onChange={(event) => {
        const raw = event.target.value
        setTyped(raw)
        const normalised = raw.replace(',', '.').trim()
        if (normalised === '') return onChange(null)
        const parsed = Number.parseFloat(normalised)
        onChange(Number.isFinite(parsed) && parsed >= 0 ? parsed : null)
      }}
      onBlur={(event) => {
        setTyped(null)
        rest.onBlur?.(event)
      }}
    />
  )
}

export function TextArea({
  label,
  className,
  id,
  ...props
}: ComponentProps<'textarea'> & { label: string }) {
  const generated = useId()
  const areaId = id ?? generated
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={areaId} className="text-sm text-dim">
        {label}
      </label>
      <textarea
        id={areaId}
        rows={3}
        className={cn(inputBase, 'resize-none py-2', className)}
        {...props}
      />
    </div>
  )
}
